import {
  getBreakdownOptions,
  requiredMappings,
  mappingLabels,
} from "./defaults";
import {
  AggregatedTotals,
  AllocationSource,
  AllocationSplit,
  BreakdownKey,
  BreakdownRow,
  ClassifiedRecord,
  ClassifiedRecords,
  MappingKey,
  KpiAllocation,
  PeriodPreset,
  QualityMeasure,
  ReportConfiguration,
  ReportState,
  ReportingField,
  ReportingRecord,
} from "./types";

/**
 * Pure reporting maths. No React and no data fetching in this file, so the numbers can
 * be reasoned about (and tested) on their own.
 *
 * Records are keyed by field uuid. A mapping may be an empty string when the workspace has
 * no field for that role, so every read has to tolerate an absent mapping rather than
 * assuming one - that is what makes the "fill these in" state safe to render.
 */

export function getValue(
  record: ReportingRecord,
  configuration: ReportConfiguration,
  mapping: MappingKey,
): string | number | null | undefined {
  const uuid = configuration.mappings[mapping];

  return uuid ? record[uuid] : undefined;
}

export function parseNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = Number(String(value).replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseDate(value: unknown): Date | null {
  if (!value) return null;

  const text = String(value).trim();

  // Read the calendar date and deliberately ignore any time component.
  //
  // The reporting month is a calendar fact about the record, exactly as it was in the
  // source spreadsheet, so it must not depend on who is looking at it. `new Date("2026-08-01
  // 12:00:00")` parses as *local* time, and `toISOString()` then converts to UTC - which in
  // a UTC+14 timezone moves that record into July. The server currently normalises these to
  // date-only, which hides the problem, but that is an upstream invariant this module should
  // not have to rely on.
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (match) {
    const [, year, month, day] = match;

    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Converts the Budget month single-select value (`YYYY/MM`) into an allocation date. */
export function parseBudgetMonth(value: unknown): Date | null {
  if (!value) return null;

  const match = String(value)
    .trim()
    .match(/^(\d{4})\/(0[1-9]|1[0-2])$/);

  return match
    ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
    : null;
}

/** Published records use Reporting date; unpublished records fall back to Budget month. */
export function getAllocationDate(
  record: ReportingRecord,
  configuration: ReportConfiguration,
): Date | null {
  return getAllocation(record, configuration).date;
}

export function getAllocation(
  record: ReportingRecord,
  configuration: ReportConfiguration,
): { date: Date | null; source: AllocationSource | null } {
  const reportingDate = parseDate(getValue(record, configuration, "date"));
  if (reportingDate) return { date: reportingDate, source: "reportingDate" };

  const budgetMonth = parseBudgetMonth(
    getValue(record, configuration, "budgetMonth"),
  );
  return {
    date: budgetMonth,
    source: budgetMonth ? "budgetMonth" : null,
  };
}

/** `YYYY-MM` key of a date, in UTC so it matches the source strings. */
export function monthKey(date: Date): string {
  return date.toISOString().slice(0, 7);
}

export function formatPeriodLabel(period: string): string {
  return new Date(`${period}-01T00:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function periodMatches(date: Date | null, periods: string[]): boolean {
  if (!date) return false;
  if (periods.length === 0) return true;
  return periods.includes(monthKey(date));
}

/**
 * True when a dated record falls before the workspace's configured reporting floor, e.g. a
 * workspace whose source predates its platform cutover. A missing date is never excluded by
 * the floor - that is a separate "no allocation month" condition.
 */
export function isBeforeReportingStart(
  date: Date | null,
  configuration: ReportConfiguration,
): boolean {
  return Boolean(
    date &&
      configuration.reportingStartMonth &&
      monthKey(date) < configuration.reportingStartMonth,
  );
}

function groupBy<T>(items: T[], getKey: (item: T) => string): Map<string, T[]> {
  return items.reduce((groups, item) => {
    const key = getKey(item);
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
    return groups;
  }, new Map<string, T[]>());
}

/**
 * Shifts a `(year, month)` pair - `month` zero-based, as `Date.getMonth()` returns - by
 * `offset` months and formats the result as a `YYYY-MM` key, the same representation
 * `monthKey` produces. Pure integer arithmetic rather than `Date` math, so it cannot run
 * into the local-vs-UTC pitfalls `parseDate` guards against elsewhere in this file.
 */
function shiftMonthKey(year: number, month: number, offset: number): string {
  const total = month + offset;
  const normalizedYear = year + Math.floor(total / 12);
  const normalizedMonth = ((total % 12) + 12) % 12;

  return `${normalizedYear}-${String(normalizedMonth + 1).padStart(2, "0")}`;
}

/**
 * Quick period shortcuts for the period picker, e.g. Kibana/Grafana-style "last 3 months".
 * Computed from the viewer's local clock - the same notion of "today" `defaultReportView`
 * already uses for its initial month - rather than being tied to what the source actually
 * contains, so a preset still means the same thing on a quiet workspace with no current-
 * month data yet.
 */
export function getPeriodPresets(today: Date = new Date()): PeriodPreset[] {
  const year = today.getFullYear();
  const month = today.getMonth();
  const quarterStart = Math.floor(month / 3) * 3;

  return [
    {
      key: "currentMonth",
      label: "Current month",
      periods: [shiftMonthKey(year, month, 0)],
    },
    {
      key: "currentQuarter",
      label: "Current quarter",
      periods: [0, 1, 2].map((offset) =>
        shiftMonthKey(year, quarterStart + offset, 0),
      ),
    },
    {
      key: "last3Months",
      label: "Last 3 months",
      periods: [-2, -1, 0].map((offset) => shiftMonthKey(year, month, offset)),
    },
    {
      key: "yearToDate",
      label: "Year to date",
      periods: Array.from({ length: month + 1 }, (_, index) =>
        shiftMonthKey(year, index, 0),
      ),
    },
  ];
}

/** Whether `periods` is exactly the set a preset would select, regardless of order. */
export function matchesPeriodPreset(
  periods: string[],
  preset: PeriodPreset,
): boolean {
  if (periods.length !== preset.periods.length) return false;
  const selected = new Set(periods);

  return preset.periods.every((period) => selected.has(period));
}

/** Distinct status values present in the source, for the "included statuses" chips. */
export function getStatusValues(
  records: ReportingRecord[],
  configuration: ReportConfiguration,
): string[] {
  return [
    ...new Set(
      records
        .map((record) => getValue(record, configuration, "status"))
        .filter(Boolean)
        .map(String),
    ),
  ].sort();
}

/**
 * Allocated months present in the source, newest first. Selected periods are kept even when
 * neither allocation mapping has rows for them, so a configuration change never silently
 * drops the user's choice.
 */
export function getAvailablePeriods(
  records: ReportingRecord[],
  state: ReportState,
): string[] {
  const { configuration, view } = state;
  const periods = [
    ...new Set(
      records
        .map((record) => {
          const date = getAllocationDate(record, configuration);
          const status = String(
            getValue(record, configuration, "status") ?? "",
          );

          return (configuration.statusMode === "all" ||
            configuration.includedStatuses.includes(status)) &&
            recordMatchesFilters(record, date, state)
            ? date
            : null;
        })
        .filter((date): date is Date => date !== null)
        .map(monthKey),
    ),
  ].sort((a, b) => b.localeCompare(a));

  view.periods.forEach((period) => {
    if (!periods.includes(period)) periods.push(period);
  });

  return periods
    .filter(
      (period) =>
        !configuration.reportingStartMonth ||
        period >= configuration.reportingStartMonth,
    )
    .sort((a, b) => b.localeCompare(a));
}

/**
 * Multilayer creator fallback: when a record's mapped Cost type value matches a configured
 * rule, that rule's label stands in for "Unassigned" - e.g. a boosting or SaaS cost line
 * that has no influencer attached to it by nature, not by omission. Empty (the default for
 * every workspace) until an admin configures it, and only reachable once a Cost type field
 * is also mapped, so a workspace whose data has no such concept is never affected.
 */
function getCreatorFallbackLabel(
  record: ReportingRecord,
  configuration: ReportConfiguration,
): string | null {
  const costType = getValue(record, configuration, "costType");
  if (!costType) return null;

  const normalized = String(costType).trim().toLowerCase();
  const rule = configuration.creatorFallbackRules.find(
    ({ costTypeValue }) => costTypeValue.trim().toLowerCase() === normalized,
  );

  return rule?.label.trim() || null;
}

export function getDimensionValue(
  record: ReportingRecord,
  date: Date | null,
  configuration: ReportConfiguration,
  dimension: BreakdownKey,
): string {
  if (dimension === "month") return date ? monthKey(date) : "No date";
  if (dimension === "customDimension1" || dimension === "customDimension2") {
    return "";
  }

  const raw = getValue(record, configuration, dimension);
  if (raw) return String(raw);

  if (dimension === "creatorName") {
    const fallback = getCreatorFallbackLabel(record, configuration);
    if (fallback) return fallback;
  }

  return "Unassigned";
}

/** Custom dimensions are the distinct, non-empty union of their selected member fields. */
export function getDimensionValues(
  record: ReportingRecord,
  date: Date | null,
  configuration: ReportConfiguration,
  dimension: BreakdownKey,
): string[] {
  const customDimension = configuration.additionalDimensions.find(
    ({ key }) => key === dimension,
  );

  if (!customDimension) {
    return [getDimensionValue(record, date, configuration, dimension)];
  }

  return [
    ...new Set(
      customDimension.fieldUuids
        .map((uuid) => record[uuid])
        .filter((value) => value !== null && value !== undefined)
        .map((value) => String(value).trim())
        .filter(Boolean),
    ),
  ];
}

export function getFilterOptions(
  records: ReportingRecord[],
  state: ReportState,
): Record<BreakdownKey, string[]> {
  const { configuration, view } = state;

  return Object.fromEntries(
    getBreakdownOptions(configuration).map(({ key }) => [
      key,
      [
        ...new Set(
          records
            .map((record) => {
              const date = getAllocationDate(record, configuration);
              const status = String(
                getValue(record, configuration, "status") ?? "",
              );

              return (configuration.statusMode === "all" ||
                configuration.includedStatuses.includes(status)) &&
                (view.periods.length === 0 ||
                  periodMatches(date, view.periods)) &&
                !isBeforeReportingStart(date, configuration) &&
                recordMatchesFilters(record, date, state, key)
                ? getDimensionValues(record, date, configuration, key)
                : [];
            })
            .flat(),
        ),
      ].sort((a, b) =>
        key === "month" ? b.localeCompare(a) : a.localeCompare(b),
      ),
    ]),
  ) as Record<BreakdownKey, string[]>;
}

export function recordMatchesFilters(
  record: ReportingRecord,
  date: Date | null,
  state: ReportState,
  ignoredFilter?: BreakdownKey,
): boolean {
  return getBreakdownOptions(state.configuration).every(({ key }) => {
    if (key === ignoredFilter) return true;

    const selected = state.view.filters[key] ?? [];
    return (
      selected.length === 0 ||
      getDimensionValues(record, date, state.configuration, key).some((value) =>
        selected.includes(value),
      )
    );
  });
}

/**
 * Splits the source into rows that contribute to the totals and rows that do not,
 * recording why. Blank financial values are summed as zero.
 */
export function classifyRecords(
  records: ReportingRecord[],
  state: ReportState,
): ClassifiedRecords {
  const { configuration, view } = state;
  const eligible: ClassifiedRecord[] = [];
  const excluded: ClassifiedRecord[] = [];

  records.forEach((record) => {
    const status = String(getValue(record, configuration, "status") ?? "");
    const allocation = getAllocation(record, configuration);
    const { date } = allocation;
    const rawRevenue = parseNumber(getValue(record, configuration, "revenue"));
    const rawCost = parseNumber(getValue(record, configuration, "cost"));
    const reasons: string[] = [];

    if (
      configuration.statusMode === "selected" &&
      !configuration.includedStatuses.includes(status)
    ) {
      reasons.push("status");
    }
    if (!date) reasons.push("date");
    else if (!periodMatches(date, view.periods)) reasons.push("period");
    if (isBeforeReportingStart(date, configuration))
      reasons.push("beforeStart");
    if (!recordMatchesFilters(record, date, state)) reasons.push("filter");

    const classified: ClassifiedRecord = {
      record,
      status,
      date,
      allocationSource: allocation.source,
      revenue: rawRevenue ?? 0,
      cost: rawCost ?? 0,
      rawRevenue,
      rawCost,
      reasons,
    };

    if (reasons.length) excluded.push(classified);
    else eligible.push(classified);
  });

  return { eligible, excluded };
}

export function aggregate(rows: ClassifiedRecord[]): AggregatedTotals {
  const revenue = rows.reduce((sum, row) => sum + row.revenue, 0);
  const cost = rows.reduce((sum, row) => sum + row.cost, 0);
  const profit = revenue - cost;

  return {
    revenue,
    cost,
    profit,
    margin: revenue ? profit / revenue : 0,
  };
}

const emptyAllocationSplit = (
  basis: AllocationSplit["basis"],
): AllocationSplit => ({
  reportingDate: 0,
  budgetMonth: 0,
  basis,
});

const sumByAllocationSource = (
  rows: ClassifiedRecord[],
  valueOf: (row: ClassifiedRecord) => number,
  basis: AllocationSplit["basis"] = "value",
): AllocationSplit =>
  rows.reduce((split, row) => {
    if (row.allocationSource) split[row.allocationSource] += valueOf(row);
    return split;
  }, emptyAllocationSplit(basis));

export function buildKpiAllocation(rows: ClassifiedRecord[]): KpiAllocation {
  const marginDisplayValues = (allocationSource: AllocationSource): number =>
    aggregate(rows.filter((row) => row.allocationSource === allocationSource))
      .margin;

  return {
    revenue: sumByAllocationSource(rows, (row) => row.revenue),
    cost: sumByAllocationSource(rows, (row) => row.cost),
    profit: sumByAllocationSource(rows, (row) => row.revenue - row.cost),
    // The bar uses revenue weights, while the boxes show the actual margin for each source.
    margin: {
      ...sumByAllocationSource(rows, (row) => row.revenue, "revenueWeight"),
      displayValues: {
        reportingDate: marginDisplayValues("reportingDate"),
        budgetMonth: marginDisplayValues("budgetMonth"),
      },
    },
  };
}

export function getAllocationPercentages(split: AllocationSplit): {
  reportingDate: number;
  budgetMonth: number;
} {
  // Absolute values keep signed profit contributions understandable when one source is
  // negative; raw signed amounts remain available in the tooltip.
  const reportingDate = Math.abs(split.reportingDate);
  const budgetMonth = Math.abs(split.budgetMonth);
  const total = reportingDate + budgetMonth;

  if (!total) return { reportingDate: 0, budgetMonth: 0 };
  return {
    reportingDate: reportingDate / total,
    budgetMonth: budgetMonth / total,
  };
}

export function buildBreakdown(
  rows: ClassifiedRecord[],
  state: ReportState,
): BreakdownRow[] {
  const { configuration, view } = state;

  // `rawValue` carries the dimension value before display formatting (the `YYYY-MM` key for
  // a month row, rather than "March 2026") - consumers that need to look something up by it
  // (a month's target, say) can't recover that from the formatted label alone.
  const entriesOf = (row: ClassifiedRecord): { raw: string; label: string }[] =>
    getDimensionValues(row.record, row.date, configuration, view.breakdown).map(
      (value) => ({
        raw: value,
        label:
          view.breakdown === "month" && value !== "No date"
            ? formatPeriodLabel(value)
            : value,
      }),
    );

  return [
    ...groupBy(
      rows.flatMap((row) => entriesOf(row).map((entry) => ({ entry, row }))),
      ({ entry }) => entry.label,
    ).entries(),
  ]
    .map(([label, group]) => ({
      label,
      rawValue: group[0].entry.raw,
      ...aggregate(group.map(({ row }) => row)),
      allocation: buildKpiAllocation(group.map(({ row }) => row)),
    }))
    .sort((a, b) => b.revenue - a.revenue);
}

export function getBreakdownLabel(state: ReportState): string {
  return (
    getBreakdownOptions(state.configuration).find(
      (option) => option.key === state.view.breakdown,
    )?.label ?? "Dimension"
  );
}

/**
 * Completeness of the fields the report depends on, measured over the rows the current
 * status and period selection covers rather than only the eligible ones - otherwise rows
 * dropped for a missing date would never be counted as missing a date.
 */
export function buildQuality(
  classified: ClassifiedRecords,
  state: ReportState,
): { measures: QualityMeasure[]; score: number } {
  const { configuration, view } = state;

  const inScope = [...classified.eligible, ...classified.excluded].filter(
    (row) =>
      (configuration.statusMode === "all" ||
        configuration.includedStatuses.includes(row.status)) &&
      (view.periods.length === 0 ||
        !row.date ||
        periodMatches(row.date, view.periods)),
  );

  const total = inScope.length;
  const divisor = total || 1;

  const measures: QualityMeasure[] = [
    {
      label: "Allocation month",
      count: inScope.filter((r) => r.date).length,
      total,
    },
    {
      label: "Revenue populated",
      count: inScope.filter((r) => r.rawRevenue !== null).length,
      total,
    },
    {
      label: "Creator cost populated",
      count: inScope.filter((r) => r.rawCost !== null).length,
      total,
    },
    {
      label: "Creator identity",
      count: inScope.filter((r) => getValue(r.record, configuration, "creator"))
        .length,
      total,
    },
  ];

  const score =
    measures.reduce((sum, measure) => sum + measure.count / divisor, 0) /
    measures.length;

  return { measures, score };
}

/**
 * The currency the totals are expressed in, or null when the mapped money fields disagree.
 * Nothing in the workspace converts between currencies, so summing across two of them
 * would produce a meaningless number - the preview reports that instead of a total.
 */
export function getReportingCurrency(
  configuration: ReportConfiguration,
  fields: ReportingField[],
): { code: string | null; isMixed: boolean } {
  const codes = (["revenue", "cost"] as MappingKey[])
    .map((role) => configuration.mappings[role])
    .filter(Boolean)
    .map((uuid) => fields.find((field) => field.uuid === uuid)?.currencyCode)
    .filter((code): code is string => Boolean(code));

  const distinct = [...new Set(codes)];

  if (distinct.length > 1) return { code: null, isMixed: true };

  return { code: distinct[0] ?? null, isMixed: false };
}

export function validateConfiguration(
  state: ReportState,
  fields: ReportingField[],
): string[] {
  const { configuration, view } = state;
  const messages: string[] = [];

  const missing = requiredMappings.filter(
    (role) => !configuration.mappings[role],
  );

  if (missing.length) {
    messages.push(
      `Map a field for: ${missing.map((role) => mappingLabels[role]).join(", ")}.`,
    );
  }

  if (!configuration.reportName) messages.push("Report name is required.");

  if (
    configuration.mappings.revenue &&
    configuration.mappings.revenue === configuration.mappings.cost
  ) {
    messages.push("Revenue and cost must use different fields.");
  }

  if (getReportingCurrency(configuration, fields).isMixed) {
    messages.push(
      "Revenue and cost use different currencies, and the workspace does not convert between them.",
    );
  }

  if (
    configuration.statusMode === "selected" &&
    !configuration.includedStatuses.length
  ) {
    messages.push("Select at least one included status.");
  }

  if (!view.kpis.length) messages.push("Select at least one KPI.");

  return messages;
}

/**
 * Campaign names read better than campaign UUIDs in the drill-down, so the campaign name
 * has its own mapping rather than the old trick of guessing from the column letter.
 */
export function getCampaignDisplayName(
  record: ReportingRecord,
  configuration: ReportConfiguration,
): string {
  return String(
    getValue(record, configuration, "campaignName") ||
      getValue(record, configuration, "campaign") ||
      "-",
  );
}
